import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, map, merge, withLatestFrom } from 'rxjs';
import { ProjectStore } from '../../state/project/project.store';
import { TranslateService } from '@ngx-translate/core';
import { CalculationMethodValue } from '@nx-customer-apps/shared/interfaces';
import { CountryCode, ProjectType, SuccessMessages } from '@nx-customer-apps/shared/enums';
import { BuildingType, HeatLoadCalculationMethod } from '@nx-customer-apps/components';
import { AccordionService } from '../../shared/interfaces';
import { HeatloadStore } from '../../state/heatload/heatload.store';
import { CalculationMethodFormService } from '../../shared/components/calculation-method';
import { EnumDictionary } from '@nx-customer-apps/shared/utils';
import { HeatloadFormOptionsService } from './heatload-form-options.service';
import { HeatloadPrefillMapper } from '../../services';

@Injectable({
    providedIn: 'root'
})
export class CalculationMethodService implements AccordionService {
    private route = inject(ActivatedRoute);
    private translate = inject(TranslateService);
    private projectStore = inject(ProjectStore);
    private heatloadStore = inject(HeatloadStore);
    private formService = inject(CalculationMethodFormService);
    private prefillMapper = inject(HeatloadPrefillMapper);
    private formOptionsService = inject(HeatloadFormOptionsService);

    public questions$ = merge(
        this.projectStore.project$.pipe(filter(Boolean)),
        this.heatloadStore.calculationMethod$.pipe(
            filter(method => !method),
            withLatestFrom(this.projectStore.project$, (method, project) => project)
        )
    ).pipe(
        map(project => {
            if (!project) {
                return null;
            }

            const prefill = this.prefillMapper.projectToCalculationMethodPrefill(project);
            const options = {
                projectType: project.projectType as ProjectType,
                buildingType: project.building.buildingType as BuildingType,
                countryCode: project.address.countryCode as CountryCode,
                prefill
            };
            const formOptions = this.formOptionsService.getCalculationMethodFormOptions(options);
            return this.formService.getCalculationMethodQuestions(formOptions);
        })
    );

    public isPrefillNeeded(): boolean {
        return Boolean(this.getRecalculationMethod());
    }

    public getSuccessMessage(formValue: CalculationMethodValue): string {
        if (!formValue.methods.standardMethod || formValue.methods.standardMethod === HeatLoadCalculationMethod.CustomValue) {
            return '';
        }
        const config: Partial<EnumDictionary<HeatLoadCalculationMethod, SuccessMessages>> = {
            [HeatLoadCalculationMethod.LivingSpace]: SuccessMessages.LivingArea,
            [HeatLoadCalculationMethod.BuildingHull]: SuccessMessages.BuildingHull,
            [HeatLoadCalculationMethod.Consumption]: SuccessMessages.ByConsumption,
            [HeatLoadCalculationMethod.EnergyCertificate]: SuccessMessages.EnergyCertificate,
            [HeatLoadCalculationMethod.Installation]: SuccessMessages.Installation,
            [HeatLoadCalculationMethod.RoomByRoom]: SuccessMessages.RoomByRoom
        };
        return this.translate.instant(config[formValue.methods.standardMethod] as string);
    }

    private getRecalculationMethod(): string | null | undefined {
        return this.route.firstChild?.firstChild?.snapshot.queryParamMap.get('method');
    }
}
